import type { ArenaRun, CandidateResult } from './hooks/useModelArena';

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** "Today" / "Yesterday" / "5 August 2026" — a run's age is usually what you are looking for. */
export const dayLabel = (iso: string): string => {
  const day = startOfDay(new Date(iso));
  const today = startOfDay(new Date());
  const daysApart = Math.round((today.getTime() - day.getTime()) / 86_400_000);

  if (daysApart === 0) return 'Today';
  if (daysApart === 1) return 'Yesterday';
  return day.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const timeLabel = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export const dateLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { dateStyle: 'medium' });

export const dateTimeLabel = (iso: string): string => new Date(iso).toLocaleString('en-GB');

/** The same moment, short enough for a table cell. */
export const shortDateTimeLabel = (iso: string): string =>
  new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });

/** How long a run has been going. Only meaningful once it has started. */
export const elapsedLabel = (run: ArenaRun, now: Date): string | null => {
  if (run.startedAt === null) return null;
  const minutes = Math.floor((now.getTime() - new Date(run.startedAt).getTime()) / 60_000);
  return minutes < 1 ? 'just started' : `${minutes}m`;
};

/** How long a run took, once it is over. */
export const durationLabel = (run: ArenaRun): string | null => {
  if (!run.startedAt || !run.finishedAt) return null;
  const seconds = Math.round(
    (new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()) / 1000,
  );
  return seconds < 90 ? `${seconds}s` : `${Math.round(seconds / 60)}m`;
};

/** What a run measured on, which is the part that varies. Where the set lives is on the task above;
 * repeating it on every row says nothing new. */
export const measuredOn = (run: ArenaRun): string => {
  const scope = run.fixtureLimit === null ? 'every document' : `first ${run.fixtureLimit}`;
  return `${scope} · ${run.fixtures.version}`;
};

export const countLabel = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

export const modelLabel = (result: CandidateResult) =>
  `${result.modelProvider}/${result.modelName}`;
